import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faTrash,
    faPoll,
    faArrowLeft,
    faEllipsisV,
    faChevronDown,
    faChevronUp,
    faPen
} from "@fortawesome/free-solid-svg-icons";

const API = "https://api18081113.sayq.pl";

// --- SURVEY CARD COMPONENT ---
const SurveyCard = ({ survey, toggleActive, deleteSurvey, openEditModal }) => {
    const [expanded, setExpanded] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const menuRef = useRef(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMenu(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Find winning option
    const sortedOptions = [...survey.options].sort((a, b) => b.votes - a.votes);
    const winner = survey.total_votes > 0 ? sortedOptions[0] : null;
    const isActive = Number(survey.is_active) === 1;

    return (
        <div className="col">
            <div className={`card h-100 border-0 shadow-sm rounded-4 position-relative ${isActive ? 'ring-2 ring-primary' : 'bg-light'}`} style={{ transition: 'all 0.2s' }}>
                <div className="card-body d-flex flex-column p-4">

                    {/* Top Row: Icon/Label + Menu */}
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="d-flex align-items-center gap-2 text-primary fw-bold small text-uppercase ls-1">
                            <FontAwesomeIcon icon={faPoll} />
                            <span>Ankieta</span>
                        </div>
                        <div className="position-relative" ref={menuRef}>
                            <button
                                onClick={() => setShowMenu(!showMenu)}
                                className="btn btn-sm btn-light rounded-circle d-flex align-items-center justify-content-center border-0 text-secondary"
                                style={{ width: 32, height: 32 }}
                            >
                                <FontAwesomeIcon icon={faEllipsisV} />
                            </button>
                            {/* Dropdown Menu */}
                            {showMenu && (
                                <div className="position-absolute end-0 mt-2 bg-white shadow-lg rounded-3 overflow-hidden py-1" style={{ zIndex: 10, minWidth: 150 }}>
                                    <button
                                        onClick={() => { openEditModal(survey); setShowMenu(false); }}
                                        className="dropdown-item text-dark small fw-bold px-3 py-2 d-flex align-items-center gap-2 hover-bg-light"
                                    >
                                        <FontAwesomeIcon icon={faPen} /> Edytuj
                                    </button>
                                    <button
                                        onClick={() => { deleteSurvey(survey.id); setShowMenu(false); }}
                                        className="dropdown-item text-danger small fw-bold px-3 py-2 d-flex align-items-center gap-2"
                                    >
                                        <FontAwesomeIcon icon={faTrash} /> Usuń
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Question */}
                    <h5 className="fw-bold text-dark mb-1 lh-sm">{survey.question}</h5>

                    {/* Winning Option Preview */}
                    <div className="mt-3 mb-auto">
                        <span className="small text-secondary fw-bold text-uppercase" style={{ fontSize: '0.7rem' }}>Na prowadzeniu:</span>
                        <div className="fw-medium text-dark mt-1 text-truncate">
                            {winner ? (
                                <span className="text-primary">{winner.option_text} ({Math.round((winner.votes / survey.total_votes) * 100)}%)</span>
                            ) : (
                                <span className="text-muted fst-italic">Czekamy na głosy...</span>
                            )}
                        </div>
                    </div>

                    {/* Bottom Row: Votes + Toggle + Expand */}
                    <div className="d-flex align-items-center justify-content-between mt-4 border-top pt-3">
                        {/* Vote Badge */}
                        <div
                            className="rounded-circle bg-primary-subtle text-primary fw-bold d-flex flex-column align-items-center justify-content-center"
                            style={{ width: 48, height: 48, lineHeight: 1 }}
                        >
                            <span style={{ fontSize: '1.1rem' }}>{survey.total_votes}</span>
                            <span style={{ fontSize: '0.6rem', opacity: 0.8 }}>GŁOSY</span>
                        </div>

                        {/* Actions */}
                        <div className="d-flex align-items-center gap-2">
                            {/* Active Toggle – iOS style */}
                            <label
                                className="ios-toggle"
                                title={isActive ? 'Wyłącz ankietę' : 'Włącz ankietę'}
                            >
                                <input
                                    type="checkbox"
                                    checked={isActive}
                                    onChange={() => toggleActive(survey)}
                                />
                                <span className="slider" />
                            </label>

                            {/* Expand Arrow */}
                            <button
                                onClick={() => setExpanded(!expanded)}
                                className="btn btn-sm btn-light rounded-circle d-flex align-items-center justify-content-center"
                                style={{ width: 36, height: 36 }}
                            >
                                <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} className="text-secondary" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Collapsible Options Section */}
                {expanded && (
                    <div className="card-footer bg-white border-top p-3 rounded-bottom-4 animate-slide-down">
                        <div className="d-flex flex-column gap-2">
                            {survey.options.map(opt => {
                                const percent = survey.total_votes > 0 ? Math.round((opt.votes / survey.total_votes) * 100) : 0;
                                return (
                                    <div key={opt.id} className="position-relative bg-light rounded-3 overflow-hidden" style={{ height: 32 }}>
                                        <div
                                            className="position-absolute top-0 start-0 h-100 bg-primary-subtle"
                                            style={{ width: `${percent}%`, transition: 'width 0.5s ease' }}
                                        ></div>
                                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-between align-items-center px-3">
                                            <span className="fw-medium text-dark small text-truncate pe-2">{opt.option_text}</span>
                                            <span className="fw-bold text-primary small flex-shrink-0">{opt.votes}</span>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};


export default function SurveysView({ secretId, closeView }) {
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(false);

    const [isModalOpen, setModalOpen] = useState(false);
    const [surveyToDelete, setSurveyToDelete] = useState(null); // ID of survey to delete

    // ... existing useState ...

    const confirmDelete = async () => {
        if (!surveyToDelete) return;
        try {
            await fetch(`${API}/delete-survey`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ secretId, surveyId: surveyToDelete })
            });
            fetchSurveys();
        } catch (e) {
            console.error(e);
        } finally {
            setSurveyToDelete(null);
        }
    };

    const deleteSurvey = (id) => {
        setSurveyToDelete(id);
    };

    // Create/Edit form
    const [question, setQuestion] = useState("");
    const [options, setOptions] = useState([{ id: null, text: "" }, { id: null, text: "" }]);
    const [editingId, setEditingId] = useState(null);
    const [allowMultiple, setAllowMultiple] = useState(false);

    const fetchSurveys = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await fetch(`${API}/get-surveys/${encodeURIComponent(secretId)}`);
            const data = await res.json();
            if (data.error) {
                console.error("Fetch Surveys Error:", data.error);
                // alert("Błąd pobierania ankiet: " + data.error);
            } else if (data.surveys) {
                // Prevent React from losing focus or re-rendering unnecessarily if the data is identical
                setSurveys(prev => JSON.stringify(prev) === JSON.stringify(data.surveys) ? prev : data.surveys);
            }
        } catch (e) {
            console.error(e);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        if (secretId) fetchSurveys();
    }, [secretId]);

    // Background Polling for real-time survey updates
    useEffect(() => {
        if (!secretId) return;
        const interval = setInterval(() => {
            if (!isModalOpen && !surveyToDelete) {
                fetchSurveys(true);
            }
        }, 5000);
        return () => clearInterval(interval);
    }, [secretId, isModalOpen, surveyToDelete]);

    const handleOptionChange = (idx, val) => {
        const arr = [...options];
        arr[idx].text = val;
        setOptions(arr);
    };

    const addOption = () => setOptions([...options, { id: null, text: "" }]);
    const removeOption = (idx) => {
        if (options.length <= 2) return;
        setOptions(options.filter((_, i) => i !== idx));
    };

    const handleSaveSurvey = async () => {
        if (!question.trim()) return alert("Wpisz pytanie");
        if (options.some(o => !o.text.trim())) return alert("Wypełnij wszystkie opcje");

        try {
            let url = `${API}/create-survey`;
            let body = {
                secretId,
                question,
                options: options.map(o => o.text), // Backend expects strings for create
                allowMultiple
            };

            if (editingId) {
                url = `${API}/edit-survey`;
                body = {
                    secretId,
                    surveyId: editingId,
                    question,
                    options: options.map(o => ({ id: o.id, text: o.text })), // Backend expects objects for edit
                    allowMultiple
                };
            }

            const res = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body)
            });

            if (res.ok) {
                resetForm();
                fetchSurveys();
            } else {
                const errText = await res.text();
                console.error("Server Error:", errText);
                alert("Błąd serwera: " + res.status + "\n" + errText.slice(0, 200));
            }
        } catch (e) {
            console.error(e);
            alert("Błąd połączenia: " + e.message);
        }
    };

    const resetForm = () => {
        setQuestion("");
        setOptions([{ id: null, text: "" }, { id: null, text: "" }]);
        setEditingId(null);
        setAllowMultiple(false);
        setModalOpen(false);
    };

    const openEditModal = (survey) => {
        setQuestion(survey.question);
        setOptions(survey.options.map(o => ({ id: o.id, text: o.option_text })));
        setEditingId(survey.id);
        setAllowMultiple(Number(survey.allow_multiple) === 1);
        setModalOpen(true);
    };

    const openCreateModal = () => {
        resetForm();
        setModalOpen(true);
    };

    const toggleActive = async (s) => {
        const newVal = Number(s.is_active) === 1 ? false : true;
        // Optimistic UI update: only change the specific survey being toggled
        const updated = surveys.map(x => (x.id === s.id ? { ...x, is_active: newVal ? 1 : 0 } : x));
        setSurveys(updated);

        await fetch(`${API}/toggle-survey`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ secretId, surveyId: s.id, isActive: newVal })
        });
        fetchSurveys();
    };

    // Old deleteSurvey removed (replaced by state version above)


    return (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-gray-50 bg-white" style={{ zIndex: 2000, overflowY: 'auto' }}>
            {/* Header - Full Width */}
            <div className="w-100 px-4 py-3 border-bottom bg-white d-flex justify-content-between align-items-center sticky-top shadow-sm">
                <button
                    onClick={closeView}
                    className="btn btn-light rounded-circle d-flex align-items-center justify-content-center transition-transform hover-scale"
                    style={{ width: 44, height: 44 }}
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                </button>

                <h2 className="fw-bold m-0 text-dark">Ankiety</h2>

                <button
                    onClick={openCreateModal}
                    className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center shadow-sm hover-lift"
                    style={{ width: 44, height: 44 }}
                >
                    <FontAwesomeIcon icon={faPlus} />
                </button>
            </div>

            <div className="container-fluid py-5 px-4 px-md-5">

                {/* MODAL OVERLAY */}
                {isModalOpen && (
                    <div
                        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{ zIndex: 3000, backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)' }}
                    >
                        <div className="bg-white rounded-4 shadow-lg p-4 w-100 m-3 position-relative animate-fade-in-up" style={{ maxWidth: 500 }}>
                            <button
                                onClick={resetForm}
                                className="btn btn-sm btn-light rounded-circle position-absolute top-0 end-0 m-3"
                                style={{ width: 32, height: 32 }}
                            >
                                ✕
                            </button>

                            <h4 className="fw-bold mb-4 text-center">{editingId ? 'Edytuj ankietę' : 'Nowe głosowanie'}</h4>

                            <div className="mb-3">
                                <label className="small text-secondary fw-bold text-uppercase ls-1">Pytanie</label>
                                <input
                                    className="form-control border bg-light py-3 rounded-3 mt-1"
                                    placeholder="O co zapytamy publikę?"
                                    value={question}
                                    onChange={e => setQuestion(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            <div className="mb-4">
                                <label className="small text-secondary fw-bold text-uppercase ls-1 mb-2 d-block">Opcje odpowiedzi</label>
                                <div className="d-flex flex-column gap-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {options.map((opt, i) => (
                                        <div key={i} className="d-flex gap-2">
                                            <input
                                                className="form-control border bg-light py-2 rounded-3"
                                                placeholder={`Opcja ${i + 1}`}
                                                value={opt.text}
                                                onChange={e => handleOptionChange(i, e.target.value)}
                                            />
                                            {options.length > 2 && (
                                                <button onClick={() => removeOption(i)} className="btn btn-outline-danger border-0 px-2">
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <button onClick={addOption} className="btn btn-sm btn-link text-decoration-none fw-bold mt-2 ps-0">
                                    <FontAwesomeIcon icon={faPlus} className="me-1" /> Dodaj kolejną opcję
                                </button>
                            </div>

                            <div className="mb-4 d-flex align-items-center gap-2">
                                <div className="form-check form-switch m-0 d-flex align-items-center gap-2">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="allowMultipleSwitch"
                                        checked={allowMultiple}
                                        onChange={(e) => setAllowMultiple(e.target.checked)}
                                        style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                                    />
                                    <label className="form-check-label fw-bold text-dark mb-0 pt-1" htmlFor="allowMultipleSwitch" style={{ cursor: 'pointer' }}>
                                        Zezwól na odpowiedzi wielokrotne
                                    </label>
                                </div>
                            </div>

                            <div className="d-grid">
                                <button onClick={handleSaveSurvey} className="btn btn-primary rounded-pill fw-bold py-3 shadow-sm hover-lift">
                                    {editingId ? 'Zapisz zmiany' : 'Publikuj'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* DELETE CONFIRMATION MODAL */}
                {surveyToDelete && (
                    <div
                        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{ zIndex: 3100, backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)' }}
                    >
                        <div className="bg-white rounded-4 shadow-lg p-4 m-3 animate-bounce-in text-center" style={{ maxWidth: 400, width: '100%' }}>
                            <div className="mb-3 text-danger opacity-75">
                                <FontAwesomeIcon icon={faTrash} size="3x" />
                            </div>
                            <h4 className="fw-bold mb-2">Usunąć ankietę?</h4>
                            <p className="text-secondary small mb-4">Tej operacji nie można cofnąć. Wszystkie głosy zostaną utracone.</p>

                            <div className="d-flex gap-2 justify-content-center">
                                <button
                                    onClick={() => setSurveyToDelete(null)}
                                    className="btn btn-light rounded-pill px-4 fw-bold"
                                >
                                    Anuluj
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="btn btn-danger rounded-pill px-4 fw-bold shadow-sm"
                                >
                                    Usuń
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* List - Tile Grid */}
                {loading && <div className="text-center py-5 text-secondary">Ładowanie...</div>}

                <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-4">
                    {surveys.map(s => (
                        <SurveyCard
                            key={s.id}
                            survey={s}
                            toggleActive={toggleActive}
                            deleteSurvey={deleteSurvey}
                            openEditModal={openEditModal}
                        />
                    ))}
                </div>

                {!loading && surveys.length === 0 && (
                    <div className="text-start py-5">
                        <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 80, height: 80 }}>
                            <FontAwesomeIcon icon={faPoll} className="text-secondary opacity-50 fs-1" />
                        </div>
                        <h5 className="text-muted fw-bold">Pusto w eterze...</h5>
                        <p className="text-secondary small">Zadaj pytanie, a uczestnicy na pewno odpowiedzą! Kliknij "+" u góry.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
